import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, ShieldCheck, ArrowRight, CheckCircle2, XCircle, PauseCircle } from 'lucide-react';
import WorkspaceLoader from './WorkspaceLoader';
import { getPlanDisplayName, isTrialPlan, isFacultyPlanScope } from '@/utils/planHelpers';

interface SubscriptionData {
  status: string;
  plan: string;
  expiry?: any;
  linkedFaculty?: string;
  allowedEmails?: string[];
  maxSeats?: number;
}

const StatusPill = ({ status }: { status: string }) => {
  const map: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    active:  { label: 'Active',   color: 'text-green-700 bg-green-100 border-green-200 dark:text-green-400 dark:bg-green-900/30 dark:border-green-700/30', icon: <CheckCircle2 size={12} /> },
    trial:   { label: 'On Trial', color: 'text-blue-700 bg-blue-100 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-900/30',   icon: <Clock size={12} /> },
    paused:  { label: 'Paused',   color: 'text-amber-700 bg-amber-100 border-amber-200 dark:text-amber-400 dark:bg-amber-900/30 dark:border-amber-700/30',icon: <PauseCircle size={12} /> },
    expired: { label: 'Expired',  color: 'text-red-700 bg-red-100 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700/30',      icon: <XCircle size={12} /> },
  };
  const s = map[status] ?? map['expired'];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[9px] font-black uppercase tracking-widest border ${s.color}`}>
      {s.icon} {s.label}
    </span>
  );
};

const IndependentAccountView = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaceName, setWorkspaceName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    setUserEmail(user.email || '');

    const unsub = onSnapshot(doc(db, 'teaching_workspaces', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSub(data.subscription || null);
        setWorkspaceName(data.displayName || '');
      }
      
      // Fetch payment history to derive the effective plan
      getDocs(query(collection(db, `teaching_workspaces/${user.uid}/transactions`), orderBy('completedAt', 'desc')))
        .then(snap => {
          setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        })
        .catch(console.error);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
    return () => unsub();
  }, [auth.currentUser]);

  const getDaysRemaining = () => {
    if (!sub?.expiry?.toDate) return null;
    const diff = sub.expiry.toDate().getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getExpiryString = () => {
    if (!sub?.expiry?.toDate) return 'No expiry set';
    return sub.expiry.toDate().toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Single Source of Truth: We strictly rely on the Firestore document's `plan` field.
  // We removed the transaction-history override because it caused race conditions
  // where users would be temporarily downgraded or upgraded if their payment history
  // conflicted with the admin/webhook's final decision in Firestore.
  const effectivePlan = sub?.plan || '';

  // True only when the plan is a paid (non-trial) cycle and Firestore says active.
  const isPaidPlan =
    sub?.status === 'active' &&
    !!effectivePlan &&
    !effectivePlan.includes('trial') &&
    (
      effectivePlan.includes('monthly') ||
      effectivePlan.includes('termly') ||
      effectivePlan.includes('yearly') ||
      effectivePlan === 'faculty_member'
    );

  const resolvedStatus = () => {
    if (!sub) return 'expired';
    if (sub.status === 'paused') return 'paused';
    // Only promote to 'active' when Firestore explicitly says so.
    if (sub.status === 'active') return 'active';
    if (sub.expiry?.toDate && new Date() > sub.expiry.toDate()) return 'expired';
    return sub.status || 'expired';
  };

  const isFacultyPlan = effectivePlan === 'faculty_member';
  // isSchoolPlan: true ONLY for an active, paid faculty/school subscription.
  // school_trial users get a separate isFacultyTrialing path, not isSchoolPlan.
  const isSchoolPlan = isFacultyPlanScope(effectivePlan) && sub?.status === 'active' && !isTrialPlan(effectivePlan);
  // isFacultyTrialing: on a faculty trial — can preview features but not a full school plan owner.
  const isFacultyTrialing = effectivePlan === 'school_trial' && sub?.status === 'trial';
  const daysLeft = getDaysRemaining();

  if (loading) {
    return <WorkspaceLoader minHeight="min-h-[400px]" />;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-4 px-3 sm:px-4">
      {/* Page Header */}
      <div className="border-b border-slate-200 dark:border-white/5 pb-6">
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-blue-500/60 mb-1">Workspace // My Account</p>
        <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white/90">{workspaceName || 'My Workspace'}</h1>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 tracking-widest">{userEmail}</p>
      </div>

      {/* Subscription Status Card */}
      <div className="bg-white border border-slate-200 dark:bg-[#040a1f]/50 dark:border-white/10 p-6 space-y-5 shadow-lg dark:shadow-2xl dark:shadow-blue-900/10">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Current Plan</p>
            <h2 className="text-lg font-black text-slate-900 dark:text-white/90 tracking-tight">
              {getPlanDisplayName(effectivePlan)}
            </h2>
          </div>
          <StatusPill status={resolvedStatus()} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100 dark:border-white/5">
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Expiry Date</p>
            <p className="text-[11px] font-bold text-slate-700 dark:text-zinc-300 tracking-wide">{getExpiryString()}</p>
          </div>
          {daysLeft !== null && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Days Remaining</p>
              <p className={`text-[11px] font-bold tracking-wide ${daysLeft <= 2 ? 'text-red-600 dark:text-red-400' : daysLeft <= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                {daysLeft === 0 ? 'Expires today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
              </p>
            </div>
          )}
        </div>

        {/* Faculty Link Info — for teachers linked to a principal's faculty */}
        {isFacultyPlan && sub?.linkedFaculty && (
          <div className="pt-3 border-t border-slate-100 dark:border-white/5">
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Linked Faculty</p>
            <p className="text-[10px] font-mono text-blue-600 dark:text-blue-400 tracking-wider">{sub.linkedFaculty}</p>
            <p className="text-[9px] text-slate-500 dark:text-slate-600 mt-1 tracking-wide">Access granted by your principal's Faculty Plan subscription.</p>
          </div>
        )}

        {/* Faculty Seats — only for active paid school plans */}
        {isSchoolPlan && (
          <div className="pt-3 border-t border-slate-100 dark:border-white/5">
            <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-1">Faculty Seats</p>
            <div className="flex items-center gap-2">
              <Users size={12} className="text-blue-600 dark:text-blue-400" />
              <p className="text-[10px] font-bold text-slate-700 dark:text-zinc-300">
                {(sub?.allowedEmails || []).length} / {sub?.maxSeats ?? '∞'} seats allocated
              </p>
            </div>
          </div>
        )}

        {/* Faculty Trial notice — for school_trial users */}
        {isFacultyTrialing && (
          <div className="pt-3 border-t border-slate-100 dark:border-white/5 flex items-start gap-2">
            <Clock size={12} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[9px] text-amber-600 dark:text-amber-400 tracking-wide">
              You are on a Faculty Trial. Faculty management features are available during your trial. Upgrade to a paid Faculty Plan before expiry to keep access.
            </p>
          </div>
        )}
      </div>

      {/* Feature Access Summary */}
      <div className="bg-slate-50 border border-slate-200 dark:bg-[#040a1f]/30 dark:border-white/5 p-6">
        <p className="text-[8px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-4">What's Included</p>
        <ul className="space-y-2.5">
          {[
          { label: 'AI Lesson Planning', unlocked: true },
            { label: 'Resource Library', unlocked: true },
            { label: 'AI Research Lab', unlocked: true },
            { label: 'Manual & AI Assessments', unlocked: true },
            { label: 'PDF Converter & Utilities', unlocked: true },
            { label: 'School Pack (Faculty Management)', unlocked: isSchoolPlan || isFacultyPlan },
            { label: 'Custom School Website (Design, Security & Maintenance)', unlocked: isSchoolPlan || isFacultyPlan },
          ].map((f) => (
            <li key={f.label} className="flex items-center gap-3">
              {f.unlocked
                ? <CheckCircle2 size={12} className="text-blue-600 dark:text-blue-500 shrink-0" />
                : <XCircle size={12} className="text-slate-400 dark:text-slate-600 shrink-0" />}
              <span className={`text-[10px] font-bold uppercase tracking-widest ${f.unlocked ? 'text-slate-700 dark:text-zinc-300' : 'text-slate-400 dark:text-slate-600'}`}>
                {f.label}
              </span>
              {!f.unlocked && (
                <span className="text-[8px] text-blue-500/40 uppercase tracking-widest ml-auto">Faculty Plan Only</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      {resolvedStatus() === 'active' ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 dark:bg-emerald-600/10 dark:border-emerald-500/20 p-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
              {getPlanDisplayName(effectivePlan)} — Active
            </p>
            <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-0.5 tracking-wide">
              {daysLeft !== null
                ? `Your plan is current — ${daysLeft} day${daysLeft === 1 ? '' : 's'} of access remaining.`
                : 'Your subscription is active.'}
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 shrink-0"
          >
            Manage Plan <ArrowRight size={12} />
          </button>
        </div>
      ) : (resolvedStatus() === 'trial' || resolvedStatus() === 'expired') && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-blue-50 border border-blue-200 dark:bg-blue-600/10 dark:border-blue-500/20 p-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-700 dark:text-blue-300">
              {resolvedStatus() === 'expired' ? 'Subscription Required' : 'Trial Active'}
            </p>
            <p className="text-[9px] text-slate-500 dark:text-slate-400 mt-0.5 tracking-wide">
              {resolvedStatus() === 'expired'
                ? 'Upgrade to restore full platform access.'
                : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left — upgrade now to keep uninterrupted access.`}
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 shrink-0"
          >
            {resolvedStatus() === 'expired' ? 'Subscribe' : 'Upgrade'} <ArrowRight size={12} />
          </button>
        </div>
      )}

      {/* Security Info */}
      <div className="flex items-center gap-3 text-[9px] text-slate-400 dark:text-slate-600 font-bold uppercase tracking-widest pt-2">
        <ShieldCheck size={14} className="text-slate-400 dark:text-slate-500" />
        <span>Payments are processed securely.</span>
      </div>

      {/* Faculty Settings — only for active paid school plan owners, not trial users */}
      {isSchoolPlan && (
        <button
          onClick={() => navigate('/dashboard/billing?panel=faculty')}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 w-full justify-center"
        >
          Manage Faculty Settings <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
};

export default IndependentAccountView;
